import backSfxUrl from './assets/sounds/BACK.wav';
import selectSfxUrl from './assets/sounds/SELECT.wav';
import confirmSfxUrl from './assets/sounds/CONFIRMATION.wav';
import gameboyBackSfxUrl from './assets/sounds/gameboy/BACK.wav';
import gameboySelectSfxUrl from './assets/sounds/gameboy/SELECT.wav';
import gameboyConfirmSfxUrl from './assets/sounds/gameboy/CONFIRMATION.wav';
import n64BackSfxUrl from './assets/sounds/n64/BACK.wav';
import n64SelectSfxUrl from './assets/sounds/n64/SELECT.wav';
import n64ConfirmSfxUrl from './assets/sounds/n64/CONFIRMATION.wav';
import xboxBackSfxUrl from './assets/sounds/xbox/BACK.wav';
import xboxSelectSfxUrl from './assets/sounds/xbox/SELECT.wav';
import xboxConfirmSfxUrl from './assets/sounds/xbox/CONFIRMATION.wav';
import snesBackSfxUrl from './assets/sounds/snes/BACK.wav';
import snesSelectSfxUrl from './assets/sounds/snes/SELECT.wav';
import snesConfirmSfxUrl from './assets/sounds/snes/CONFIRMATION.wav';
import mgsConfirmSfxUrl from './assets/sounds/mgs/CONFIRMATION.wav';
import mgsSelectSfxUrl from './assets/sounds/mgs/SELECT.wav';
import mgsBackSfxUrl from './assets/sounds/mgs/BACK.wav';
import gcnBackSfxUrl from './assets/sounds/gcn/BACK.wav';
import gcnSelectSfxUrl from './assets/sounds/gcn/SELECT.wav';
import gcnConfirmSfxUrl from './assets/sounds/gcn/CONFIRMATION.wav';
import xmbConfirmSfxUrl from './assets/sounds/xmb/CONFIRMATION.mp3';
import xmbSelectSfxUrl from './assets/sounds/xmb/SELECT.mp3';
import xmbBackSfxUrl from './assets/sounds/xmb/BACK.mp3';
import gbaBackSfxUrl from './assets/sounds/gba/BACK.wav';
import gbaSelectSfxUrl from './assets/sounds/gba/SELECT.wav';
import gbaConfirmSfxUrl from './assets/sounds/gba/CONFIRMATION.wav';
import genConfirmSfxUrl from './assets/sounds/gen/CONFIRMATION.wav';
import genSelectSfxUrl from './assets/sounds/gen/SELECT.wav';
import genBackSfxUrl from './assets/sounds/gen/BACK.wav';
import wiiBackSfxUrl from './assets/sounds/wii/BACK.wav';
import wiiSelectSfxUrl from './assets/sounds/wii/SELECT.wav';
import wiiConfirmSfxUrl from './assets/sounds/wii/CONFIRMATION.wav';
import dsConfirmSfxUrl from './assets/sounds/ds/CONFIRMATION.wav';
import dsSelectSfxUrl from './assets/sounds/ds/SELECT.wav';
import dsBackSfxUrl from './assets/sounds/ds/BACK.wav';
import saturnBackSfxUrl from './assets/sounds/saturn/BACK.wav';
import saturnSelectSfxUrl from './assets/sounds/saturn/SELECT.wav';
import saturnConfirmSfxUrl from './assets/sounds/saturn/CONFIRMATION.wav';

export const ACTIVE_CONTROLLER_SFX_COLLECTION_KEY = 'games:active-controller-sfx-collection';
const CONTROLLER_SOUND_POOL_SIZE = 16;

let sharedControllerAudioContext = null;
const sharedControllerBufferCache = new Map();
let controllerAudioUnlockHandlersBound = false;

const bindControllerAudioUnlockHandlers = () => {
  if (controllerAudioUnlockHandlersBound || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  controllerAudioUnlockHandlersBound = true;

  const tryResume = () => {
    if (!sharedControllerAudioContext) return;
    if (sharedControllerAudioContext.state === 'suspended') {
      sharedControllerAudioContext.resume().catch(() => {});
    }
  };

  window.addEventListener('pointerdown', tryResume, { passive: true });
  window.addEventListener('keydown', tryResume);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      tryResume();
    }
  });
};

const getControllerAudioContext = () => {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (sharedControllerAudioContext && sharedControllerAudioContext.state === 'closed') {
    sharedControllerAudioContext = null;
  }
  if (!sharedControllerAudioContext) {
    sharedControllerAudioContext = new AudioContextClass();
    bindControllerAudioUnlockHandlers();
  }
  return sharedControllerAudioContext;
};

const decodeAudioBuffer = async (audioContext, arrayBuffer) => {
  const bufferCopy = arrayBuffer.slice(0);
  if (typeof audioContext.decodeAudioData === 'function') {
    return new Promise((resolve, reject) => {
      const maybePromise = audioContext.decodeAudioData(
        bufferCopy,
        (decodedBuffer) => resolve(decodedBuffer),
        (error) => reject(error)
      );
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(resolve, reject);
      }
    });
  }
  throw new Error('Web Audio API decodeAudioData is unavailable');
};

const loadAudioBuffer = async (url) => {
  const audioContext = getControllerAudioContext();
  if (!audioContext) return null;
  const response = await fetch(url, { cache: 'force-cache' });
  if (!response.ok) {
    throw new Error(`Failed to load controller sound: ${url}`);
  }
  const arrayBuffer = await response.arrayBuffer();
  return decodeAudioBuffer(audioContext, arrayBuffer);
};

const getOrCreateBufferPromise = (url) => {
  if (!sharedControllerBufferCache.has(url)) {
    sharedControllerBufferCache.set(
      url,
      loadAudioBuffer(url).catch(() => null)
    );
  }
  return sharedControllerBufferCache.get(url);
};

export const CONTROLLER_SFX_COLLECTIONS = {
  default: {
    back: backSfxUrl,
    select: selectSfxUrl,
    confirm: confirmSfxUrl,
  },
  gameboy: {
    back: gameboyBackSfxUrl,
    select: gameboySelectSfxUrl,
    confirm: gameboyConfirmSfxUrl,
  },
  n64: {
    back: n64BackSfxUrl,
    select: n64SelectSfxUrl,
    confirm: n64ConfirmSfxUrl,
  },
  xbox: {
    back: xboxBackSfxUrl,
    select: xboxSelectSfxUrl,
    confirm: xboxConfirmSfxUrl,
  },
  snes: {
    back: snesBackSfxUrl,
    select: snesSelectSfxUrl,
    confirm: snesConfirmSfxUrl,
  },
  mgs: {
    back: mgsBackSfxUrl,
    select: mgsSelectSfxUrl,
    confirm: mgsConfirmSfxUrl,
  },
  gcn: {
    back: gcnBackSfxUrl,
    select: gcnSelectSfxUrl,
    confirm: gcnConfirmSfxUrl,
  },
  xmb: {
    back: xmbBackSfxUrl,
    select: xmbSelectSfxUrl,
    confirm: xmbConfirmSfxUrl,
  },
  gba: {
    back: gbaBackSfxUrl,
    select: gbaSelectSfxUrl,
    confirm: gbaConfirmSfxUrl,
  },
  gen: {
    back: genBackSfxUrl,
    select: genSelectSfxUrl,
    confirm: genConfirmSfxUrl,
  },
  wii: {
    back: wiiBackSfxUrl,
    select: wiiSelectSfxUrl,
    confirm: wiiConfirmSfxUrl,
  },
  saturn: {
    back: saturnBackSfxUrl,
    select: saturnSelectSfxUrl,
    confirm: saturnConfirmSfxUrl,
  },
  ds: {
    back: dsBackSfxUrl,
    select: dsSelectSfxUrl,
    confirm: dsConfirmSfxUrl,
  },
  // Add more collections here as needed
  // Add custom collections here:
  // retro: { back: backRetroUrl, select: selectRetroUrl, confirm: confirmRetroUrl },
};

export const PLATFORM_SFX_COLLECTION_BY_ID = {
  19: 'gameboy', // Game Boy
  28: 'default', // Dreamcast
  20: 'n64', // Nintendo 64
  15: 'xbox', // Xbox
  8: 'xbox', // Xbox 360
  21: 'xbox', // Xbox One
  1: 'snes', // Super Nintendo
  18: 'mgs', // PlayStation
  30: 'mgs', // PlayStation 2
  29: 'gcn', // GameCube
  32: 'xmb', // PlayStation Portable
  6: 'xmb', // PlayStation 3
  11: 'gba', // Game Boy Advance
  39: 'gen', // Sega Genesis
  27: 'wii', // Wii
  3: 'ds', // Nintendo DS
  17: 'saturn', // Sega Saturn
  // '<platformId>': 'retro',
};

export const createControllerSoundSet = (collectionKey) => {
  const selectedCollection =
    CONTROLLER_SFX_COLLECTIONS[collectionKey] || CONTROLLER_SFX_COLLECTIONS.default;

  const createSlot = (url) => {
    const pool = [];
    for (let i = 0; i < CONTROLLER_SOUND_POOL_SIZE; i += 1) {
      const audio = new Audio(url);
      audio.preload = 'auto';
      audio.load();
      pool.push(audio);
    }
    const slot = {
      url,
      pool,
      poolCursor: 0,
      buffer: null,
      bufferPromise: null,
    };
    slot.bufferPromise = getOrCreateBufferPromise(url)
      .then((buffer) => {
        slot.buffer = buffer;
        return buffer;
      })
      .catch(() => null);
    return slot;
  };

  return {
    back: createSlot(selectedCollection.back),
    select: createSlot(selectedCollection.select),
    confirm: createSlot(selectedCollection.confirm),
  };
};

export const warmControllerSounds = (collectionKey) => {
  const selectedCollection =
    CONTROLLER_SFX_COLLECTIONS[collectionKey] || CONTROLLER_SFX_COLLECTIONS.default;
  getOrCreateBufferPromise(selectedCollection.back);
  getOrCreateBufferPromise(selectedCollection.select);
  getOrCreateBufferPromise(selectedCollection.confirm);
  return getControllerAudioContext();
};

Object.keys(CONTROLLER_SFX_COLLECTIONS).forEach((collectionKey) => {
  warmControllerSounds(collectionKey);
});

export const playControllerSoundFromSet = (soundSet, soundName) => {
  if (!soundSet) return;
  const slot = soundSet[soundName];
  if (!slot) return;

  const audioContext = getControllerAudioContext();
  if (audioContext && slot.buffer) {
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }
    if (audioContext.state === 'running') {
      try {
        const source = audioContext.createBufferSource();
        source.buffer = slot.buffer;
        source.connect(audioContext.destination);
        source.start(0);
        return;
      } catch (_err) {
        // fall back to HTMLAudio pool below
      }
    }
  }

  if (audioContext && slot.bufferPromise) {
    slot.bufferPromise.then(() => {
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
    });
  }

  const sound = slot.pool[slot.poolCursor];
  if (!sound) return;
  slot.poolCursor = (slot.poolCursor + 1) % slot.pool.length;
  try {
    sound.pause();
    sound.currentTime = 0;
    sound.play().catch(() => {});
  } catch (_err) {
    // no-op: best effort SFX playback
  }
};

export const resolvePlatformSfxCollectionKey = (nextPlatformId) =>
  PLATFORM_SFX_COLLECTION_BY_ID[String(nextPlatformId)] || null;
